import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Ban, Brain, Briefcase, ChevronDown, ChevronRight, Download, FileText, GitCompare, Mail, Star, ThumbsDown, ThumbsUp, Trophy, Users, X } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import CandidateEmailSendConsentModal from "../components/CandidateEmailSendConsentModal";
import {
  localizedCriteriaReason,
  localizedLanguageEducationDetail,
  scoringLabel,
  scoringLanguageFromText,
} from "../utils/localizedScoringLabels";

export default function JobRankings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(jobId || "");
  const [jobData, setJobData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [rankingViewMode, setRankingViewMode] = useState("grouped");
  const [collapsedGroups, setCollapsedGroups] = useState({
    shortlisted: false,
    rejected: false,
    review: false,
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [activeDecisionTabs, setActiveDecisionTabs] = useState({});
  const [notes, setNotes] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [savingFeedbackId, setSavingFeedbackId] = useState(null);
  const [loadingAnalysisId, setLoadingAnalysisId] = useState(null);
  const [loadingQuestionId, setLoadingQuestionId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [selectedActionIds, setSelectedActionIds] = useState([]);
  const [sendingEmailType, setSendingEmailType] = useState(null);
  const [emailModalType, setEmailModalType] = useState(null);
  const emailConsentResolverRef = useRef(null);
  const [emailSendConsent, setEmailSendConsent] = useState({
    open: false,
    provider: "",
  });
  const [showCompare, setShowCompare] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const canSendCandidateEmails = ["pro", "enterprise"].includes(user?.plan);

  useLongActionNotice(Boolean(sendingEmailType), "Candidate email sending", 45000);
  useLongActionNotice(Boolean(loadingAnalysisId), "AI analysis", 45000);
  useLongActionNotice(Boolean(loadingQuestionId), "Generate interview questions", 45000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    setSelectedJobId(jobId || "");
  }, [jobId]);

  useEffect(() => {
    if (!user) return;

    const fetchJobs = async () => {
      try {
        const res = await apiFetch("/jobs");
        const data = await res.json();

        if (!res.ok) {
          toast.error(data?.detail || "Failed to load jobs.");
          return;
        }

        const availableJobs = Array.isArray(data) ? data : [];
        setJobs(availableJobs);

        if (!jobId && availableJobs.length) {
          setSelectedJobId(String(availableJobs[0].id));
        }
      } catch (err) {
        toast.error("Failed to load jobs.");
      }
    };

    fetchJobs();
  }, [user, jobId]);

  useEffect(() => {
    if (!user) return;

    if (!selectedJobId) {
      setJobData(null);
      setFetching(false);
      return;
    }

    const fetchRankings = async () => {
      setFetching(true);

      try {
        const res = await apiFetch(`/jobs/${selectedJobId}/details`);
        const data = await res.json();

        if (!res.ok) {
          toast.error(data?.detail || "Failed to load job rankings.");
          navigate("/jobs");
          return;
        }

        setJobData(data);
        setNotes(
          (data.candidates || []).reduce((acc, candidate) => {
            if (candidate.recruiter_note) acc[candidate.scan_id] = candidate.recruiter_note;
            return acc;
          }, {})
        );
      } catch (err) {
        toast.error("Failed to load job rankings.");
      } finally {
        setFetching(false);
      }
    };

    fetchRankings();
  }, [user, selectedJobId, navigate]);

  const job = jobData?.job || {};
  const candidates = jobData?.candidates || [];
  const scoringLanguage = scoringLanguageFromText(job?.description || "");
  const isArchived = Boolean(job.is_archived);
  const readyScanIds = useMemo(() => {
    const rawIds = searchParams.get("ready_scan_ids") || "";

    return new Set(
      rawIds
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    );
  }, [searchParams]);
  const hasSimulatorFilter = readyScanIds.size > 0;

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return candidates.filter((candidate) => {
      if (hasSimulatorFilter && !readyScanIds.has(String(candidate.scan_id))) {
        return false;
      }

      const hasAi = Boolean(candidate.ai_summary);
      const matchesFilter =
        filter === "all" ||
        (filter === "shortlisted" && candidate.is_shortlisted) ||
        (filter === "ai" && hasAi) ||
        (filter === "review" && !candidate.is_shortlisted);

      if (!matchesFilter) return false;
      if (!normalizedQuery) return true;

      const searchableText = [
        candidate.filename,
        candidate.top_qualification,
        candidate.recruiter_note,
        candidate.ai_summary,
        ...(candidate.matching_skills || []),
        ...(candidate.missing_skills || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [candidates, filter, hasSimulatorFilter, query, readyScanIds]);

  const stats = useMemo(() => {
    const total = candidates.length;
    const shortlisted = candidates.filter((candidate) => candidate.is_shortlisted).length;
    const aiEnriched = candidates.filter((candidate) => candidate.ai_summary).length;
    const averageScore = total
      ? Math.round(candidates.reduce((sum, candidate) => sum + (candidate.match_score || 0), 0) / total)
      : 0;

    return { total, shortlisted, aiEnriched, averageScore };
  }, [candidates]);

  const comparedCandidates = useMemo(
    () => candidates.filter((candidate) => compareIds.includes(candidate.scan_id)),
    [candidates, compareIds]
  );

  const currentTopMatches = useMemo(
    () => [...candidates]
      .sort((a, b) => (b.match_score || 0) - (a.match_score || 0))
      .slice(0, 5),
    [candidates]
  );

  const isShortlistedEmailSent = (candidate) =>
    candidate.is_shortlisted &&
    candidate.email_status === "sent" &&
    candidate.email_type === "shortlisted";

  const isRejectedEmailSent = (candidate) =>
    candidate.is_rejected &&
    candidate.email_status === "sent" &&
    candidate.email_type === "rejected";

  const isDecisionLocked = (candidate) =>
    isShortlistedEmailSent(candidate) || isRejectedEmailSent(candidate);

  const confirmDecisionUnlock = (candidate) => {
    if (!isDecisionLocked(candidate)) return true;

    const decision = isShortlistedEmailSent(candidate) ? "shortlist" : "rejection";
    return window.confirm(
      `A ${decision} email has already been sent for this candidate. Changing the decision will reactivate the candidate record, but it will not undo the email already prepared/sent.\n\nDo you want to continue?`
    );
  };

  const groupedRankingRows = useMemo(() => {
    const shortlistedCandidates = filteredCandidates.filter((candidate) => candidate.is_shortlisted);
    const rejectedCandidates = filteredCandidates.filter((candidate) => candidate.is_rejected);
    const reviewCandidates = filteredCandidates.filter(
      (candidate) => !candidate.is_shortlisted && !candidate.is_rejected
    );

    const rows = [];
    const addGroup = (key, title, description, tone, groupCandidates) => {
      if (!groupCandidates.length) return;

      rows.push({
        __group: true,
        key,
        title,
        description,
        tone,
        count: groupCandidates.length,
      });
      rows.push(...groupCandidates);
    };

    addGroup(
      "shortlisted",
      "Shortlisted",
      "Candidates selected by the recruiter. Email status is shown inside each row.",
      "emerald",
      shortlistedCandidates
    );
    addGroup(
      "rejected",
      "Rejected",
      "Candidates marked as not moving forward. Email status is shown inside each row.",
      "rose",
      rejectedCandidates
    );
    addGroup(
      "review",
      "Needs review",
      "Candidates still waiting for recruiter action.",
      "slate",
      reviewCandidates
    );

    return rows;
  }, [filteredCandidates]);

  const displayedRankingRows = rankingViewMode === "grouped" ? groupedRankingRows : filteredCandidates;

  const toggleRankingGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const isLanguageEvidenceItem = (item) => {
    const terms = [
      "german",
      "deutsch",
      "english",
      "englisch",
      "spanish",
      "french",
      "italian",
      "tamil",
      "c1",
      "b2",
      "b1",
      "a2",
      "a1",
      "native speaker",
      "muttersprache",
    ];
    const text = [
      item?.skill,
      item?.language,
      item?.required_level,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return terms.some((term) => text.includes(term));
  };

  const isValidLanguageMatchItem = (item) => {
    if (!item || typeof item !== "object") return false;
    return isLanguageEvidenceItem(item);
  };

  const getSkillEvidence = (candidate) =>
    Array.isArray(candidate.skill_evidence)
      ? candidate.skill_evidence.filter((item) => !isLanguageEvidenceItem(item))
      : [];

  const getLanguageMatch = (candidate) => {
    const languageRows = Array.isArray(candidate.language_match)
      ? candidate.language_match.filter(isValidLanguageMatchItem)
      : [];
    const migratedRows = Array.isArray(candidate.skill_evidence)
      ? candidate.skill_evidence.filter(isLanguageEvidenceItem).map((item) => ({
          language: item.language || item.skill || "Language",
          required_level: item.required_level || "Not specified",
          fit_score: item.fit_score ?? 0,
          confidence: item.confidence || 0,
          status: item.status || "language signal",
          evidence: item.evidence || "",
        }))
      : [];

    return [...languageRows, ...migratedRows];
  };

  const getAiStrengths = (candidate) =>
    candidate.llm_strengths || candidate.candidate_summary?.llm_strengths || [];

  const getAiGaps = (candidate) =>
    candidate.llm_gaps || candidate.candidate_summary?.llm_gaps || [];

  const getScoringBreakdown = (candidate) =>
    candidate.scoring_breakdown || candidate.candidate_summary?.scoring_breakdown || null;

  const getTopQualification = (candidate) =>
    candidate.top_qualification || candidate.candidate_summary?.top_qualification || "";

  const getLanguageFitScore = (item) =>
    Math.max(0, Math.min(100, Number(item?.fit_score ?? 0) || 0));

  const getWeightedScoreBreakdown = (candidate) => {
    const breakdown = getScoringBreakdown(candidate);
    const languageRows = getLanguageMatch(candidate);
    const qualification = getTopQualification(candidate);
    const finalScore = Math.round(candidate.match_score || 0);

    const languageFitPercent = languageRows.length
      ? languageRows.reduce((sum, item) => sum + getLanguageFitScore(item), 0) / languageRows.length
      : null;
    const educationPercent = qualification ? 60 : 0;
    const educationLanguagePercent = Math.max(languageFitPercent ?? 0, educationPercent);
    const languageEducationDetail = localizedLanguageEducationDetail(qualification, languageRows, scoringLanguage);

    const toPoints = (value, max) => Math.round((Math.max(0, Math.min(100, Number(value) || 0)) / 100) * max);
    const languagePoints = toPoints(educationLanguagePercent, 10);
    const experiencePercent = breakdown?.experience_score ?? 0;
    const hasMustCriteria = breakdown?.must_score !== null && breakdown?.must_score !== undefined;
    const hasPreferredCriteria = breakdown?.preferred_score !== null && breakdown?.preferred_score !== undefined;

    if (!hasMustCriteria && !hasPreferredCriteria) {
      const semanticPercent = breakdown?.semantic_score ?? breakdown?.base_score ?? finalScore;
      const semanticPoints = Math.min(toPoints(semanticPercent, 30), finalScore);
      const supportPoints = Math.min(languagePoints, Math.max(0, finalScore - semanticPoints));
      const experiencePoints = Math.min(toPoints(experiencePercent, 25), Math.max(0, finalScore - semanticPoints - supportPoints));
      const skillPoints = Math.max(0, finalScore - semanticPoints - experiencePoints - supportPoints);

      return [
        {
          label: scoringLabel("Semantic relevance", scoringLanguage),
          points: semanticPoints,
          max: 30,
          available: true,
          detail: "How closely the CV text matches the job description overall, even when wording differs.",
        },
        {
          label: scoringLabel("Skill coverage", scoringLanguage),
          points: skillPoints,
          max: 35,
          available: true,
          detail: "How much of the detected job skill requirement is supported by skills found in the CV.",
        },
        {
          label: scoringLabel("Experience evidence", scoringLanguage),
          points: experiencePoints,
          max: 25,
          available: experiencePercent > 0,
          detail: "Whether required skills are proven inside work experience or project context, not only listed as keywords.",
        },
        {
          label: scoringLabel("Language / education", scoringLanguage),
          points: supportPoints,
          max: 10,
          available: supportPoints > 0,
          detail: languageEducationDetail,
        },
      ];
    }

    const rows = [
      {
        label: scoringLabel("Semantic relevance", scoringLanguage),
        points: toPoints(breakdown?.semantic_score ?? breakdown?.base_score ?? candidate.match_score ?? 0, 25),
        max: 25,
        available: true,
        detail: "How closely the CV text matches the job description before must-have and preferred criteria are applied.",
      },
      {
        label: scoringLabel("Must-have skills", scoringLanguage),
        points: toPoints(breakdown?.must_score ?? 0, 35),
        max: 35,
        available: hasMustCriteria,
        detail: "Recruiter-selected critical skills. Weak evidence here can cap the final score.",
      },
      {
        label: scoringLabel("Preferred skills", scoringLanguage),
        points: toPoints(breakdown?.preferred_score ?? 0, 15),
        max: 15,
        available: hasPreferredCriteria,
        detail: "Bonus skills that improve confidence, but should not reject a candidate by themselves.",
      },
      {
        label: scoringLabel("Experience evidence", scoringLanguage),
        points: toPoints(experiencePercent, 20),
        max: 20,
        available: experiencePercent > 0,
        detail: "Whether required skills are proven inside work experience or project context, not only listed as keywords.",
      },
      {
        label: scoringLabel("Language / education", scoringLanguage),
        points: toPoints(educationLanguagePercent, 5),
        max: 5,
        available: languageFitPercent !== null || Boolean(qualification),
        detail: languageEducationDetail,
      },
    ];

    return rows;
  };

  const renderScoreBreakdown = (candidate) => {
    const score = Math.round(candidate.match_score || 0);
    const rows = getWeightedScoreBreakdown(candidate);
    const breakdown = getScoringBreakdown(candidate);
    const needsHumanReview = Boolean(breakdown?.needs_human_review);

    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-emerald-950">{scoringLabel("Score Breakdown", scoringLanguage)}</p>
            <p className="mt-1 text-xs leading-5 text-emerald-700">
              {scoringLabel("Score rows show independent evidence signals. The final score is calculated separately from the full scoring model.", scoringLanguage)}
            </p>
          </div>
          <span className="w-fit rounded-full bg-white px-3 py-1 text-sm font-bold text-emerald-900">
            {scoringLabel("Final", scoringLanguage)} {score}%
          </span>
        </div>

        <div className="mt-3 rounded-md bg-white/85 p-3 font-mono text-sm text-slate-800">
          <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-slate-500">
            {scoringLabel("Breakdown", scoringLanguage)}
          </p>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={`${candidate.scan_id}-${row.label}`} className="grid grid-cols-[1fr_auto] items-center gap-4">
                <span className="truncate font-sans font-medium text-slate-700">{row.label}</span>
                <span className={`font-bold tabular-nums ${row.available ? "text-slate-950" : "text-slate-400"}`}>
                  {row.available ? `${row.points}/${row.max}` : `${scoringLabel("Not set", scoringLanguage)} /${row.max}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3 rounded-md border border-emerald-100 bg-white/70 p-3 text-xs leading-5 text-emerald-900">
          <span className="font-semibold">{scoringLabel("How to read this:", scoringLanguage)}</span>{" "}
          {scoringLabel("semantic relevance shows broad CV-to-JD fit; skill coverage shows required stack overlap; experience evidence checks whether skills appear in work/project context; language or education adds a support signal.", scoringLanguage)}
        </div>

        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950 shadow-sm">
          <span className="font-bold">Important:</span>{" "}
          AI enrichment explains selected candidates, but the recruiter remains responsible for reviewing evidence and making the final hiring decision.
        </div>

        {needsHumanReview && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
            <span className="font-semibold">Needs recruiter review:</span>{" "}
            The CV looks broadly related to the job, but RecruitGlance could not confirm the required criteria from deterministic evidence. Review the CV before making a decision.
          </div>
        )}
      </div>
    );
  };

  const hasHumanReviewFlag = (candidate) =>
    Boolean(getScoringBreakdown(candidate)?.needs_human_review);

  const getDetectedSignals = (candidate) => {
    const signals = new Set(candidate.matching_skills || []);
    const breakdown = getScoringBreakdown(candidate);

    [...(breakdown?.must_have || []), ...(breakdown?.preferred || [])].forEach((item) => {
      const score = Number(item?.score || 0);
      if (item?.skill && score > 0) {
        signals.add(`${item.skill} (${Math.round(score)}%)`);
      }
    });

    return Array.from(signals);
  };

  const getUnconfirmedSignals = (candidate) => {
    const signals = new Set(candidate.missing_skills || []);
    const breakdown = getScoringBreakdown(candidate);

    (breakdown?.must_have || []).forEach((item) => {
      if (item?.skill && !item.passed) {
        signals.add(item.skill);
      }
    });

    (breakdown?.preferred || []).forEach((item) => {
      if (item?.skill && !item.found) {
        signals.add(item.skill);
      }
    });

    return Array.from(signals);
  };

  const buildScoreExplanation = (candidate) => {
    const score = Math.round(candidate.match_score || 0);
    const matchingSkills = candidate.matching_skills || [];
    const missingSkills = candidate.missing_skills || [];
    const breakdown = getScoringBreakdown(candidate);
    const asPercent = (value) => Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
    const parts = [];

    if (breakdown) {
      const semanticScore = breakdown.semantic_score !== null && breakdown.semantic_score !== undefined
        ? asPercent(breakdown.semantic_score)
        : null;
      const baseScore = asPercent(breakdown.base_score);

      if (semanticScore !== null) {
        parts.push(`Semantic relevance is ${semanticScore}%, showing the broad CV-to-job fit before detailed evidence is reviewed.`);
      }

      if (breakdown.must_score !== null && breakdown.must_score !== undefined) {
        const failedMustCount = breakdown.failed_must_count || 0;
        const mustScore = asPercent(breakdown.must_score);
        parts.push(
          failedMustCount > 0
            ? `${failedMustCount} must-have requirement${failedMustCount === 1 ? "" : "s"} stayed below target, so the final score is limited.`
            : `Must-have criteria are mostly covered with a ${mustScore}% fit.`
        );
      }

      if (breakdown.preferred_score !== null && breakdown.preferred_score !== undefined) {
        parts.push(`Preferred criteria show ${asPercent(breakdown.preferred_score)}% bonus fit.`);
      }

      if (breakdown.experience_score !== null && breakdown.experience_score !== undefined) {
        parts.push(`Experience evidence is ${asPercent(breakdown.experience_score)}%, based on whether required skills appear in work or project context.`);
      }

      parts.push(`Baseline role fit before final weighting was ${baseScore}%.`);
    } else {
      parts.push("The score is based on semantic relevance, detected skill coverage, experience evidence, and role-specific overlap.");
    }

    if (matchingSkills.length) {
      parts.push(`Detected signals include ${matchingSkills.slice(0, 5).join(", ")}.`);
    }

    if (missingSkills.length) {
      parts.push(`Unconfirmed signals include ${missingSkills.slice(0, 5).join(", ")}.`);
    } else {
      parts.push("No major unconfirmed signals were detected from the job description.");
    }

    return `This CV received ${score}% because ${parts.join(" ")}`;
  };

  const normalizeComparisonText = (value) => String(value || "").trim().toLowerCase();

  const formatListValue = (items = [], limit = 6) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (!list.length) return "Not detected";

    const shown = list.slice(0, limit).join(", ");
    return list.length > limit ? `${shown} +${list.length - limit} more` : shown;
  };

  const compareSkillRows = useMemo(() => {
    const rows = new Map();

    comparedCandidates.forEach((candidate) => {
      [...(candidate.matching_skills || []), ...(candidate.missing_skills || [])].forEach((skill) => {
        const key = normalizeComparisonText(skill);
        if (key && !rows.has(key)) rows.set(key, skill);
      });
    });

    return Array.from(rows.values()).slice(0, 22);
  }, [comparedCandidates]);

  const getSkillComparisonCell = (candidate, skill) => {
    const key = normalizeComparisonText(skill);
    const isMatched = (candidate.matching_skills || []).some((item) => normalizeComparisonText(item) === key);
    const isMissing = (candidate.missing_skills || []).some((item) => normalizeComparisonText(item) === key);

    if (isMatched) {
      return {
        label: "Matched",
        className: "border-emerald-200 bg-emerald-50 text-emerald-700",
      };
    }

    if (isMissing) {
      return {
        label: "Gap",
        className: "border-rose-200 bg-rose-50 text-rose-700",
      };
    }

    return {
      label: "Not detected",
      className: "border-slate-200 bg-slate-50 text-slate-500",
    };
  };

  const compareCriteriaRows = useMemo(() => {
    const rows = new Map();

    comparedCandidates.forEach((candidate) => {
      const breakdown = getScoringBreakdown(candidate);

      (breakdown?.must_have || []).forEach((item) => {
        const key = normalizeComparisonText(item.skill);
        if (key) rows.set(`must-${key}`, { skill: item.skill, type: "Must-have" });
      });

      (breakdown?.preferred || []).forEach((item) => {
        const key = normalizeComparisonText(item.skill);
        if (key) rows.set(`preferred-${key}`, { skill: item.skill, type: "Preferred" });
      });
    });

    return Array.from(rows.values());
  }, [comparedCandidates]);

  const getCriteriaComparisonCell = (candidate, row) => {
    const breakdown = getScoringBreakdown(candidate);
    const list = row.type === "Must-have" ? breakdown?.must_have || [] : breakdown?.preferred || [];
    const item = list.find((entry) => normalizeComparisonText(entry.skill) === normalizeComparisonText(row.skill));

    if (!item) {
      return {
        label: "Not scored",
        detail: "",
        className: "border-slate-200 bg-slate-50 text-slate-500",
      };
    }

    const score = Math.round(Number(item.score) || 0);
    const isStrong = score >= 70;
    const isPartial = score >= 35 && score < 70;

    return {
      label: `${score}%`,
      detail: localizedCriteriaReason(item.reason || "", scoringLanguage),
      className: isStrong
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : isPartial
          ? "border-amber-200 bg-amber-50 text-amber-700"
          : "border-rose-200 bg-rose-50 text-rose-700",
    };
  };

  const toggleRow = (scanId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [scanId]: !prev[scanId],
    }));
  };

  const toggleCompare = (candidate) => {
    setCompareIds((prev) => {
      if (prev.includes(candidate.scan_id)) {
        return prev.filter((scanId) => scanId !== candidate.scan_id);
      }

      if (prev.length >= 3) {
        toast.error("You can compare up to 3 candidates at once.");
        return prev;
      }

      return [...prev, candidate.scan_id];
    });
  };

  const openCompare = () => {
    if (compareIds.length < 2) {
      toast.error("Select at least 2 candidates to compare.");
      return;
    }

    setShowCompare(true);
  };

  const toggleActionSelection = (candidate) => {
    setSelectedActionIds((prev) =>
      prev.includes(candidate.scan_id)
        ? prev.filter((scanId) => scanId !== candidate.scan_id)
        : [...prev, candidate.scan_id]
    );
  };

  const clearActionSelection = () => setSelectedActionIds([]);

  const getEmailCandidates = (emailType) => {
    if (emailType === "shortlisted") {
      return candidates.filter(
        (candidate) => candidate.is_shortlisted && !isShortlistedEmailSent(candidate)
      );
    }

    return candidates.filter(
      (candidate) => candidate.is_rejected && !isRejectedEmailSent(candidate)
    );
  };

  const openEmailModal = (emailType) => {
    if (!canSendCandidateEmails) {
      toast.error("Candidate email sending is available on Pro and Enterprise plans.");
      return;
    }

    const suggestedCandidates = getEmailCandidates(emailType);
    setSelectedActionIds(suggestedCandidates.map((candidate) => candidate.scan_id));
    setEmailModalType(emailType);
  };

  const closeEmailModal = () => {
    if (sendingEmailType) return;
    setEmailModalType(null);
  };

  const updateCandidateRow = (scanId, updates) => {
    setJobData((prev) => ({
      ...prev,
      candidates: (prev?.candidates || []).map((item) =>
        item.scan_id === scanId ? { ...item, ...updates } : item
      ),
    }));
  };

  const ensureCandidateEmailConfigured = async (emailType) => {
    const res = await apiFetch("/candidate-email-settings");
    const settings = await res.json();

    if (!res.ok) {
      toast.error(actionFailureMessage("Check candidate email settings", settings?.detail));
      return false;
    }

    const missingConfiguration =
      emailType === "shortlisted"
        ? !settings.shortlisted_template
        : !settings.rejected_template;

    if (missingConfiguration) {
      toast.error("Add the rejection email template in Profile > App Configuration before sending candidate emails.");
      return false;
    }

    if (!settings.sender_mailbox_connected || !settings.sender_mailbox_has_send_scope) {
      toast.error("Connect Gmail or Outlook with email sending permission before sending candidate emails.");
      return false;
    }

    if (settings.send_consent_required) {
      const accepted = await requestCandidateEmailSendConsent(settings.provider);
      if (!accepted) return false;

      const consentRes = await apiFetch("/candidate-email-settings/consent", { method: "POST" });
      const consentData = await consentRes.json();

      if (!consentRes.ok) {
        toast.error(actionFailureMessage("Save email sending consent", consentData?.detail));
        return false;
      }
    }

    return true;
  };

  const requestCandidateEmailSendConsent = (provider) =>
    new Promise((resolve) => {
      emailConsentResolverRef.current = resolve;
      setEmailSendConsent({
        open: true,
        provider,
      });
    });

  const closeCandidateEmailSendConsent = (accepted) => {
    const resolve = emailConsentResolverRef.current;
    emailConsentResolverRef.current = null;
    setEmailSendConsent((prev) => ({
      ...prev,
      open: false,
    }));
    resolve?.(accepted);
  };

  const toggleShortlist = async (candidate) => {
    if (!confirmDecisionUnlock(candidate)) return;

    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/shortlist`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Shortlist update", data?.detail));
        return;
      }

      updateCandidateRow(candidate.scan_id, {
        is_shortlisted: data.is_shortlisted,
        is_rejected: data.is_rejected,
      });
    } catch (err) {
      toast.error(actionFailureMessage("Shortlist update"));
    }
  };

  const toggleRejected = async (candidate) => {
    if (!confirmDecisionUnlock(candidate)) return;

    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/reject`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Rejection update", data?.detail));
        return;
      }

      updateCandidateRow(candidate.scan_id, {
        is_rejected: data.is_rejected,
        is_shortlisted: data.is_shortlisted,
      });
    } catch {
      toast.error(actionFailureMessage("Rejection update"));
    }
  };

  const sendBatchEmails = async (emailType) => {
    if (!canSendCandidateEmails) {
      toast.error("Candidate email sending is available on Pro and Enterprise plans.");
      return;
    }

    if (!selectedActionIds.length) {
      toast.error("Select candidates first.");
      return;
    }

    const isConfigured = await ensureCandidateEmailConfigured(emailType);
    if (!isConfigured) return;

    setSendingEmailType(emailType);

    try {
      const res = await apiFetch("/dashboard/my_scans/send-emails", {
        method: "POST",
        body: JSON.stringify({
          scan_ids: selectedActionIds,
          email_type: emailType,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Candidate email sending", data?.detail));
        return;
      }

      (data.results || []).forEach((item) => {
        if (item.status === "skipped") return;

        updateCandidateRow(item.scan_id, {
          email_status: item.status,
          email_type: item.email_type || emailType,
          email_sent_at: item.email_sent_at,
          email_error: item.error,
        });
      });

      const summary = `Emails sent: ${data.sent_count}. Failed/missing: ${data.failed_count}. Skipped: ${data.skipped_count}.`;
      if (data.failed_count > 0) {
        const firstFailure = (data.results || []).find((item) =>
          ["failed", "missing_email"].includes(item.status)
        );
        const failureReason = firstFailure?.error ? ` Reason: ${firstFailure.error}` : "";
        toast.error(`${summary}${failureReason}`);
      } else {
        toast.success(summary);
        setEmailModalType(null);
      }
    } catch {
      toast.error(actionFailureMessage("Candidate email sending"));
    } finally {
      setSendingEmailType(null);
    }
  };

  const renderEmailStatus = (candidate) => {
    if (!candidate.email_status) {
      return <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">Email not sent</span>;
    }

    const typeLabel = candidate.email_type === "rejected" ? "rejection" : "shortlist";
    const statusLabel =
      candidate.email_status === "sent"
        ? `${typeLabel} email sent`
        : candidate.email_status === "missing_email"
          ? "Missing email - retry after adding email"
          : candidate.email_status === "failed"
            ? "Email failed - retry available"
            : candidate.email_status.replace("_", " ");
    const tone =
      candidate.email_status === "sent"
        ? "border border-emerald-300 bg-emerald-600 text-white shadow-sm shadow-emerald-100"
        : candidate.email_status === "missing_email"
          ? "bg-amber-100 text-amber-800"
          : "bg-rose-100 text-rose-700";

    return (
      <span
        className={`rounded-full px-2 py-1 text-xs font-semibold ${tone}`}
        title={candidate.email_error || statusLabel}
      >
        {statusLabel}
      </span>
    );
  };

  const saveNote = async (scanId) => {
    setSavingNoteId(scanId);

    try {
      const res = await apiFetch(`/dashboard/my_scans/${scanId}/note`, {
        method: "POST",
        body: JSON.stringify({ note: notes[scanId] || "" }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Save note", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        candidates: (prev?.candidates || []).map((item) =>
          item.scan_id === scanId
            ? { ...item, recruiter_note: notes[scanId] || "" }
            : item
        ),
      }));
      toast.success("Note saved.");
    } catch (err) {
      toast.error(actionFailureMessage("Save note"));
    } finally {
      setSavingNoteId(null);
    }
  };

  const saveFeedback = async (candidate, feedback) => {
    setSavingFeedbackId(candidate.scan_id);

    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/feedback`, {
        method: "POST",
        body: JSON.stringify({
          feedback,
          reason: notes[candidate.scan_id] || candidate.recruiter_note || "",
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Save feedback", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        candidates: (prev?.candidates || []).map((item) =>
          item.scan_id === candidate.scan_id
            ? {
                ...item,
                recruiter_feedback: data.feedback,
                recruiter_feedback_reason: data.reason,
              }
            : item
        ),
      }));
      toast.success("Feedback saved for model training.");
    } catch (err) {
      toast.error(actionFailureMessage("Save feedback"));
    } finally {
      setSavingFeedbackId(null);
    }
  };

  const requestAiAnalysis = async (candidate) => {
    setLoadingAnalysisId(candidate.scan_id);

    try {
      const res = await apiFetch(`/scan/${candidate.scan_id}/summary`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("AI analysis", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        candidates: (prev?.candidates || []).map((item) =>
          item.scan_id === candidate.scan_id
            ? {
                ...item,
                ai_summary: data.ai_summary,
                llm_strengths: data.llm_strengths || data.strengths || [],
                llm_gaps: data.llm_gaps || data.gaps || [],
                interview_questions: data.interview_questions || [],
                skill_evidence: data.skill_evidence || [],
                language_match: data.language_match || [],
                top_qualification: data.top_qualification || item.top_qualification,
                scoring_breakdown: data.scoring_breakdown || item.scoring_breakdown,
              }
            : item
        ),
      }));

      toast.success(data?.charged ? "AI analysis generated. 1 scan was used from your quota." : "AI analysis loaded.");
    } catch (err) {
      toast.error(actionFailureMessage("AI analysis"));
    } finally {
      setLoadingAnalysisId(null);
    }
  };

  const requestInterviewQuestions = async (candidate) => {
    setLoadingQuestionId(candidate.scan_id);

    try {
      const res = await apiFetch(`/scan/${candidate.scan_id}/interview-questions`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Generate interview questions", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        candidates: (prev?.candidates || []).map((item) =>
          item.scan_id === candidate.scan_id
            ? {
                ...item,
                interview_questions: data.interview_questions || [],
              }
            : item
        ),
      }));

      toast.success("Interview questions generated.");
    } catch (err) {
      toast.error(actionFailureMessage("Generate interview questions"));
    } finally {
      setLoadingQuestionId(null);
    }
  };

  const scoreClass = (score = 0) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-rose-200 bg-rose-50 text-rose-700";
  };

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const csvEscape = (value) => {
    const text = Array.isArray(value) ? value.join("; ") : String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const htmlEscape = (value) => {
    const text = Array.isArray(value) ? value.join("; ") : String(value ?? "");
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getExportRows = () =>
    filteredCandidates.map((candidate, index) => ({
      rank: index + 1,
      candidate: candidate.filename,
      score: Math.round(candidate.match_score || 0),
      status: candidate.is_shortlisted ? "Shortlisted" : candidate.is_rejected ? "Rejected" : "Needs review",
      scanned_date: formatDate(candidate.created_at),
      matching_skills: candidate.matching_skills || [],
      missing_skills: candidate.missing_skills || [],
      ai_summary: candidate.ai_summary || "",
      recruiter_note: candidate.recruiter_note || "",
      email_status: candidate.email_status || "",
    }));

  const downloadCsvReport = () => {
    if (!filteredCandidates.length) {
      toast.error("No ranking rows available to export.");
      return;
    }

    const headers = [
      "Rank",
      "Candidate",
      "Score",
      "Status",
      "Scanned Date",
      "Detected Skill Evidence",
      "Unconfirmed Skill Evidence",
      "AI Summary",
      "Recruiter Note",
      "Email Status",
    ];
    const rows = getExportRows().map((row) => [
      row.rank,
      row.candidate,
      `${row.score}%`,
      row.status,
      row.scanned_date,
      row.matching_skills,
      row.missing_skills,
      row.ai_summary,
      row.recruiter_note,
      row.email_status,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const jobName = (job.name || "job").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

    link.href = url;
    link.download = `${jobName || "job"}-ranking-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openPdfReport = () => {
    if (!filteredCandidates.length) {
      toast.error("No ranking rows available to export.");
      return;
    }

    const rows = getExportRows();
    const htmlRows = rows.map((row) => `
      <tr>
        <td>${row.rank}</td>
        <td>${htmlEscape(row.candidate)}</td>
        <td>${row.score}%</td>
        <td>${htmlEscape(row.status)}</td>
        <td>${htmlEscape(row.scanned_date)}</td>
        <td>${htmlEscape(row.matching_skills)}</td>
        <td>${htmlEscape(row.missing_skills)}</td>
      </tr>
    `).join("");
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      toast.error("Popup blocked. Allow popups to export PDF.");
      return;
    }

    printWindow.opener = null;
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${htmlEscape(job.name || "Job")} Ranking Report</title>
          <style>
            body { font-family: Arial, sans-serif; color: #0f172a; margin: 32px; }
            h1 { margin: 0 0 8px; font-size: 24px; }
            p { color: #475569; margin: 0 0 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #dbe4ee; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #ecfdf5; color: #065f46; }
            .summary { display: flex; gap: 12px; margin: 18px 0; }
            .summary div { border: 1px solid #dbe4ee; border-radius: 8px; padding: 10px 14px; }
            @media print { button { display: none; } body { margin: 18px; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()" style="margin-bottom:16px;padding:10px 14px;border-radius:8px;border:1px solid #dbe4ee;background:#064e3b;color:white;font-weight:700;">Save as PDF</button>
          <h1>${htmlEscape(job.name || "Job")} Ranking Report</h1>
          <p>Generated from RecruitGlance rankings. ${rows.length} candidate${rows.length === 1 ? "" : "s"} included.</p>
          <div class="summary">
            <div><strong>Scanned</strong><br>${stats.total}</div>
            <div><strong>Shortlisted</strong><br>${stats.shortlisted}</div>
            <div><strong>AI enriched</strong><br>${stats.aiEnriched}</div>
            <div><strong>Average score</strong><br>${stats.averageScore}%</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Candidate</th>
                <th>Score</th>
                <th>Status</th>
                <th>Scanned date</th>
                <th>Matching skills</th>
                <th>Missing skills</th>
              </tr>
            </thead>
            <tbody>${htmlRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const leftPanel = <RecruiterSidebar activeItem="rankings" user={user} />;
  const rankingsHeaderAction = (
    <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end">
      {[
        { label: "Scanned", value: stats.total, icon: Users },
        { label: "Shortlisted", value: stats.shortlisted, icon: Star },
        { label: "AI enriched", value: stats.aiEnriched, icon: Brain },
        { label: "Avg. score", value: `${stats.averageScore}%`, icon: Trophy },
      ].map((item) => {
        const Icon = item.icon;

        return (
          <div key={`rankings-header-${item.label}`} className="min-w-[124px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{item.label}</span>
              <Icon size={14} className="text-emerald-700" />
            </div>
            <p className="mt-1 text-lg font-bold text-slate-950">{item.value}</p>
          </div>
        );
      })}
    </div>
  );

  const mainContent = (
    <>
      <section className="overflow-hidden rounded-lg border border-emerald-900/20 bg-[#031b14] text-white shadow-sm">
        <div className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
              <Trophy size={14} />
              Job rankings
            </div>
            <h2 className="mt-4 truncate text-2xl font-semibold tracking-tight">
              {job.name || "Job"}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-emerald-50/80">
              One consolidated ranking across every scan batch for this job.
            </p>
          </div>

          <div className="w-full space-y-3 lg:w-[320px] lg:justify-self-end">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-100/80">Select job</span>
              <select
                value={selectedJobId}
                onChange={(event) => {
                  setSelectedJobId(event.target.value);
                  setSearchParams({});
                  setQuery("");
                  setFilter("all");
                  setExpandedRows({});
                }}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-900/30"
              >
                <option value="">Choose a job to review rankings</option>
                {isArchived && selectedJobId && (
                  <option value={selectedJobId}>
                    {job.name} (Archived)
                  </option>
                )}
                {jobs.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => {
                if (isArchived) {
                  toast.error("Restore this job before scanning more candidates.");
                  return;
                }

                if (selectedJobId) navigate(`/bulk-scan?job_id=${selectedJobId}`);
              }}
              disabled={!selectedJobId || isArchived}
              className="inline-flex min-h-[42px] w-full items-center justify-center rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isArchived ? "Job Archived" : "Scan More Candidates"}
            </button>
          </div>
        </div>
      </section>

      {currentTopMatches.length > 0 && (
        <section className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-950">Current Top Matches</h3>
              <p className="mt-1 text-sm text-slate-500">
                Best candidates for this job across all scan batches.
              </p>
            </div>
            <span className="w-fit rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-800">
              Live ranking
            </span>
          </div>

          <div className="grid gap-3 lg:grid-cols-5">
            {currentTopMatches.map((candidate, index) => (
              <button
                key={`current-top-${candidate.scan_id}`}
                type="button"
                onClick={() => setExpandedRows((prev) => ({ ...prev, [candidate.scan_id]: true }))}
                className="rounded-lg border border-emerald-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-800 text-sm font-bold text-white">
                    #{index + 1}
                  </span>
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${scoreClass(candidate.match_score || 0)}`}>
                    {Math.round(candidate.match_score || 0)}%
                  </span>
                </div>
                <p className="mt-3 line-clamp-2 min-h-[40px] text-sm font-semibold text-slate-900">
                  {candidate.filename}
                </p>
                <p className="mt-2 text-xs font-medium text-slate-500">
                  {candidate.ai_summary ? "AI analysis ready" : "Basic score only"}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-white p-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-950">Candidate Results</h3>
            <p className="mt-1 text-sm text-slate-500">
              Sorted by match score across all scan batches.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search results"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 sm:w-72"
            />

            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
            >
              <option value="all">All results</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="ai">AI enriched</option>
              <option value="review">Needs review</option>
            </select>

            {rankingViewMode === "grouped" ? (
              <button
                type="button"
                onClick={() => {
                  setRankingViewMode("ranking");
                  setSearchParams({});
                  setQuery("");
                  setFilter("all");
                  setExpandedRows({});
                }}
                className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Reset ranking view
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setRankingViewMode("grouped")}
                className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
              >
                Group by decision
              </button>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={() => setExportMenuOpen((prev) => !prev)}
                disabled={!filteredCandidates.length}
                className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                <Download size={16} />
                Export
                <ChevronDown size={15} />
              </button>

              {exportMenuOpen && filteredCandidates.length > 0 && (
                <div className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setExportMenuOpen(false);
                      downloadCsvReport();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Download size={16} />
                    Download CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExportMenuOpen(false);
                      openPdfReport();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FileText size={16} />
                    Download PDF
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={openCompare}
              disabled={compareIds.length < 2}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <GitCompare size={16} />
              Compare {compareIds.length ? `(${compareIds.length})` : ""}
            </button>
            <button
              type="button"
              onClick={() => openEmailModal("rejected")}
              disabled={!canSendCandidateEmails || getEmailCandidates("rejected").length === 0 || sendingEmailType !== null}
              title={
                !canSendCandidateEmails
                  ? "Upgrade to Pro or Enterprise to send candidate emails."
                  : getEmailCandidates("rejected").length === 0
                    ? "Reject candidates before sending rejection emails."
                    : "Review rejection email recipients"
              }
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Mail size={16} />
              Email Rejected
            </button>
          </div>
        </div>

        <div className="mx-5 mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
          Rankings are ordered by match score across all scan rounds. AI reasoning is generated for the top candidates in each scan batch and can be added manually for any candidate.
        </div>

        {hasSimulatorFilter && (
          <div className="mx-5 mt-5 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing candidates marked ready by the Criteria Simulator.
            </span>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="text-left text-sm font-semibold text-emerald-900 underline-offset-2 hover:underline"
            >
              Show all rankings
            </button>
          </div>
        )}

        {compareIds.length > 0 && (
          <div className="mx-5 mt-5 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {compareIds.length} candidate{compareIds.length === 1 ? "" : "s"} selected for comparison.
            </span>
            <button
              type="button"
              onClick={() => {
                setCompareIds([]);
                setShowCompare(false);
              }}
              className="text-left text-sm font-semibold text-emerald-900 underline-offset-2 hover:underline"
            >
              Clear selection
            </button>
          </div>
        )}

        {selectedActionIds.length > 0 && (
          <div className="mx-5 mt-5 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {selectedActionIds.length} candidate{selectedActionIds.length === 1 ? "" : "s"} selected for email actions.
            </span>
            <button
              type="button"
              onClick={clearActionSelection}
              className="text-left text-sm font-semibold text-slate-700 underline-offset-2 hover:underline"
            >
              Clear email selection
            </button>
          </div>
        )}

        {displayedRankingRows.length === 0 ? (
          <div className="m-5 rounded-lg border border-dashed border-slate-200 p-8 text-center">
            <Briefcase className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-sm font-medium text-slate-700">
              {selectedJobId ? "No scanned candidates found" : "Select a job first"}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {selectedJobId
                ? "Scan imported applicants to build the job ranking."
                : "Choose a job to review its consolidated candidate ranking."}
            </p>
          </div>
        ) : (
          <div className="mt-5">
            <div className="hidden border-y border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 lg:grid lg:grid-cols-[minmax(300px,1.5fr)_120px_minmax(220px,1fr)_130px_auto] lg:items-center lg:gap-4">
              <span>Candidate</span>
              <span>AI Score</span>
              <span>Key Evidence</span>
              <span>Last Activity</span>
              <span className="text-right">Actions</span>
            </div>
            {displayedRankingRows.map((row, index) => {
              if (row.__group) {
                const toneClass =
                  row.tone === "emerald"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                    : row.tone === "rose"
                      ? "border-rose-200 bg-rose-50 text-rose-950"
                      : "border-slate-200 bg-slate-50 text-slate-950";

                const isCollapsed = Boolean(collapsedGroups[row.key]);

                return (
                  <button
                    type="button"
                    key={`ranking-group-${row.key}`}
                    onClick={() => toggleRankingGroup(row.key)}
                    className={`mx-4 my-3 w-[calc(100%-2rem)] rounded-lg border px-4 py-3 text-left transition hover:shadow-sm ${toneClass}`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-slate-700">
                          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </span>
                        <div>
                          <p className="text-sm font-bold">{row.title}</p>
                        <p className="mt-1 text-xs leading-5 opacity-80">{row.description}</p>
                        </div>
                      </div>
                      <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold">
                        {row.count} candidate{row.count === 1 ? "" : "s"}
                      </span>
                    </div>
                  </button>
                );
              }

              const candidate = row;
              const previousGroups = displayedRankingRows
                .slice(0, index)
                .filter((item) => item.__group);
              const currentGroup = previousGroups[previousGroups.length - 1];
              if (rankingViewMode === "grouped" && currentGroup && collapsedGroups[currentGroup.key]) {
                return null;
              }

              const isExpanded = Boolean(expandedRows[candidate.scan_id]);
              const topQualification = getTopQualification(candidate);
              const lockedDecision = isDecisionLocked(candidate);
              const needsHumanReview = hasHumanReviewFlag(candidate);
              const rankingPosition = candidates.findIndex((item) => item.scan_id === candidate.scan_id) + 1;
              const hasAiEvidence =
                candidate.ai_summary ||
                getAiStrengths(candidate).length > 0 ||
                getAiGaps(candidate).length > 0 ||
                getSkillEvidence(candidate).length > 0 ||
                (candidate.interview_questions || []).length > 0;
              const scoringBreakdown = getScoringBreakdown(candidate);
              const hasCriteriaEvidence =
                (scoringBreakdown?.must_have || []).length > 0 || (scoringBreakdown?.preferred || []).length > 0;
              const rankingDecisionTabs = [
                { id: "technical", label: "Skills & Evidence", disabled: false },
                { id: "ai", label: hasAiEvidence ? "AI Review" : "AI Review · Not generated", disabled: false },
                ...(getLanguageMatch(candidate).length > 0 ? [{ id: "language", label: "Language / Education", disabled: false }] : []),
                ...(hasCriteriaEvidence ? [{ id: "role", label: "Criteria Match", disabled: false }] : []),
              ];
              const selectedDecisionTab = rankingDecisionTabs.some(
                (tab) => tab.id === activeDecisionTabs[candidate.scan_id] && !tab.disabled
              )
                ? activeDecisionTabs[candidate.scan_id]
                : "technical";
              const decisionTabClass = (tab) =>
                `inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border px-3 text-xs font-bold transition ${
                  selectedDecisionTab === tab.id
                    ? "border-emerald-900 bg-emerald-950 text-white shadow-sm"
                    : tab.disabled
                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                }`;

              return (
                <article
                  key={candidate.scan_id}
                  className={`border-b border-slate-100 bg-white transition last:border-b-0 hover:bg-slate-50/70 ${
                    lockedDecision
                      ? "bg-slate-50/80"
                      : candidate.is_shortlisted
                        ? "bg-emerald-50/30"
                        : ""
                  }`}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleRow(candidate.scan_id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleRow(candidate.scan_id);
                      }
                    }}
                    className="grid cursor-pointer grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[minmax(300px,1.5fr)_120px_minmax(220px,1fr)_130px_auto] lg:items-center lg:gap-4"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedActionIds.includes(candidate.scan_id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleActionSelection(candidate)}
                        className="mt-2 h-4 w-4 rounded border-slate-300 text-emerald-700"
                        aria-label={`Select ${candidate.filename} for email action`}
                      />
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-sm font-bold text-emerald-800 ring-1 ring-emerald-100">
                        #{rankingPosition || index + 1}
                      </span>

                      <div className="min-w-0">
                        <h4 className="truncate font-semibold text-slate-950">{candidate.filename}</h4>
                        <p className="mt-1 text-xs font-medium text-slate-400">
                          Scanned {formatDate(candidate.created_at)}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {topQualification && (
                            <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                              Education & certifications: {topQualification}
                            </span>
                          )}
                          {candidate.ai_summary && (
                            <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                              AI enriched
                            </span>
                          )}
                          {needsHumanReview && (
                            <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-bold text-amber-900 shadow-sm">
                              Needs recruiter review
                            </span>
                          )}
                          {candidate.is_shortlisted && (
                            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                              Shortlisted
                            </span>
                          )}
                          {candidate.is_rejected && (
                            <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-700">
                              Rejected
                            </span>
                          )}
                          {lockedDecision && (
                            <span className="rounded-full border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700">
                              Decision locked
                            </span>
                          )}
                          {renderEmailStatus(candidate)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center lg:justify-start">
                      <span className={`inline-flex min-w-[76px] justify-center rounded-lg border px-3 py-2 text-sm font-bold ${scoreClass(candidate.match_score || 0)}`}>
                        {Math.round(candidate.match_score || 0)}%
                      </span>
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap gap-2">
                        {getDetectedSignals(candidate).slice(0, 4).map((skill) => (
                          <span key={`${candidate.scan_id}-row-signal-${skill}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {skill}
                          </span>
                        ))}
                        {getDetectedSignals(candidate).length > 4 && (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                            +{getDetectedSignals(candidate).length - 4}
                          </span>
                        )}
                        {getDetectedSignals(candidate).length === 0 && (
                          <span className="text-sm text-slate-500">No confirmed evidence yet</span>
                        )}
                      </div>
                    </div>

                    <p className="text-sm font-medium text-slate-500 lg:text-slate-600">
                      {formatDate(candidate.created_at)}
                    </p>

                    <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleCompare(candidate);
                        }}
                        className={`inline-flex min-h-[34px] items-center justify-center rounded-lg px-3 text-sm font-semibold transition ${
                          compareIds.includes(candidate.scan_id)
                            ? "bg-emerald-800 text-white hover:bg-emerald-900"
                            : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                        }`}
                      >
                        {compareIds.includes(candidate.scan_id) ? "Selected" : "Compare"}
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleShortlist(candidate);
                        }}
                        className={`inline-flex min-h-[34px] items-center justify-center rounded-lg px-3 text-sm font-semibold transition ${
                          candidate.is_shortlisted
                            ? "bg-amber-300 text-slate-950 hover:bg-amber-400"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {candidate.is_shortlisted ? "Shortlisted" : "Shortlist"}
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleRejected(candidate);
                        }}
                        className={`inline-flex min-h-[34px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${
                          candidate.is_rejected
                            ? "bg-rose-700 text-white hover:bg-rose-800"
                            : "bg-rose-50 text-rose-700 hover:bg-rose-100"
                        }`}
                      >
                        <Ban size={14} />
                        {candidate.is_rejected ? "Rejected" : "Reject"}
                      </button>

                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="grid gap-5 border-t border-slate-200 bg-slate-50 px-4 py-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.55fr)]">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
                          <p className="text-sm font-semibold text-slate-950">Why this score?</p>
                          <p className="mt-2 text-sm leading-6 text-slate-600">
                            {buildScoreExplanation(candidate)}
                          </p>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-white p-2 lg:col-span-2">
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                            {rankingDecisionTabs.map((tab) => (
                              <button
                                key={`${candidate.scan_id}-ranking-decision-tab-${tab.id}`}
                                type="button"
                                disabled={tab.disabled}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (!tab.disabled) {
                                    const scrollY = window.scrollY;
                                    setActiveDecisionTabs((prev) => ({
                                      ...prev,
                                      [candidate.scan_id]: tab.id,
                                    }));
                                    requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: window.scrollX }));
                                  }
                                }}
                                className={decisionTabClass(tab)}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {selectedDecisionTab === "technical" && (
                        <div className="rounded-xl border-2 border-emerald-400 bg-emerald-100/60 p-4 shadow-[0_0_0_3px_rgba(16,185,129,0.10)] lg:col-span-2">
                          <div className="mb-4 rounded-lg bg-emerald-950 px-4 py-3 text-white shadow-sm">
                            <p className="mt-1 text-base font-bold">Technical Fit</p>
                            <p className="mt-1 text-xs leading-5 text-emerald-100">Skills, requirement evidence, score contribution, and technical gaps.</p>
                          </div>

                          <div className="grid gap-4 lg:grid-cols-2">
                            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Detected signals</p>
                              <p className="mb-3 text-xs leading-5 text-slate-500">Confirmed by skill extraction, recruiter criteria evidence, or semantic relevance.</p>
                              <div className="flex flex-wrap gap-2">
                                {getDetectedSignals(candidate).length > 0 ? (
                                  getDetectedSignals(candidate).map((skill) => (
                                    <span key={`${candidate.scan_id}-match-${skill}`} className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                      {skill}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-500">No strong matches detected</span>
                                )}
                              </div>
                            </div>

                            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Unconfirmed signals</p>
                              <p className="mb-3 text-xs leading-5 text-slate-500">Required or useful job signals not clearly confirmed yet.</p>
                              <div className="flex flex-wrap gap-2">
                                {getUnconfirmedSignals(candidate).length > 0 ? (
                                  getUnconfirmedSignals(candidate).map((skill) => (
                                    <span key={`${candidate.scan_id}-gap-${skill}`} className="rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                                      {skill}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-500">No major gaps detected</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 rounded-lg border border-emerald-200 bg-white p-4 shadow-sm">
                            {renderScoreBreakdown(candidate)}
                          </div>
                        </div>
                        )}

                        {selectedDecisionTab === "role" && (
                        <div className="rounded-xl border-2 border-amber-400 bg-amber-100/60 p-4 shadow-[0_0_0_3px_rgba(245,158,11,0.12)] lg:col-span-2">
                          <div className="mb-4 rounded-lg bg-amber-700 px-4 py-3 text-white shadow-sm">
                            <p className="mt-1 text-base font-bold">Role & Responsibility Fit</p>
                            <p className="mt-1 text-xs leading-5 text-amber-50">Seniority, ownership, delivery context, collaboration, and leadership-style signals are reflected through experience evidence and reviewer notes.</p>
                          </div>

                          {hasCriteriaEvidence && (
                            <div className="rounded-lg border border-amber-200 bg-white p-4 shadow-sm">
                              <p className="text-sm font-semibold text-amber-950">Criteria Evidence</p>
                              <div className="grid gap-2 md:grid-cols-2">
                                {(scoringBreakdown.must_have || []).map((item) => (
                                  <div key={`${candidate.scan_id}-ranking-must-${item.skill}`} className="rounded-md border border-amber-100 bg-amber-50/60 p-3">
                                    <div className="flex items-center justify-between gap-2 text-sm">
                                      <span className="font-semibold text-slate-900">{item.skill}</span>
                                      <span className={item.passed ? "font-bold text-emerald-700" : "font-bold text-rose-700"}>
                                        {Math.round(item.score || 0)}% / {Math.round(item.required || 0)}%
                                      </span>
                                    </div>
                                    <p className="mt-1 text-xs leading-5 text-slate-600">
                                      {localizedCriteriaReason(item.reason || "No clear evidence found in CV.", scoringLanguage)}
                                    </p>
                                  </div>
                                ))}
                                {(scoringBreakdown.preferred || []).map((item) => (
                                  <div key={`${candidate.scan_id}-ranking-preferred-${item.skill}`} className="rounded-md border border-slate-100 bg-slate-50 p-3">
                                    <div className="flex items-center justify-between gap-2 text-sm">
                                      <span className="font-semibold text-slate-900">{item.skill}</span>
                                      <span className="font-bold text-slate-700">{Math.round(item.score || 0)}%</span>
                                    </div>
                                    <p className="mt-1 text-xs leading-5 text-slate-600">
                                      {localizedCriteriaReason(item.reason || "No clear evidence found in CV.", scoringLanguage)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        )}

                        {selectedDecisionTab === "ai" && (
                          <div className="rounded-xl border-2 border-indigo-400 bg-indigo-100/60 p-4 shadow-[0_0_0_3px_rgba(99,102,241,0.12)] lg:col-span-2">
                            <div className="mb-4 rounded-lg bg-indigo-950 px-4 py-3 text-white shadow-sm">
                              <p className="mt-1 text-base font-bold">AI Evidence</p>
                              <p className="mt-1 text-xs leading-5 text-indigo-100">
                                {hasAiEvidence
                                  ? "AI-generated reasoning for this candidate, including summary, evidence scores, gaps, and optional interview questions."
                                  : "Request an AI review only when the recruiter wants deeper candidate evidence."}
                              </p>
                            </div>

                            {!hasAiEvidence && (
                              <div className="rounded-lg border border-indigo-200 bg-white p-4 shadow-sm">
                                <p className="text-sm font-semibold text-indigo-950">AI analysis not generated yet</p>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                  Request AI analysis when you want deeper evidence for this candidate. This can use scan quota for non-enriched candidates.
                                </p>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    requestAiAnalysis(candidate);
                                  }}
                                  disabled={loadingAnalysisId === candidate.scan_id}
                                  className="mt-3 inline-flex min-h-[34px] items-center justify-center rounded-lg bg-emerald-800 px-3 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:opacity-60"
                                >
                                  {loadingAnalysisId === candidate.scan_id ? "Generating..." : "Get AI Analysis"}
                                </button>
                              </div>
                            )}

                            {(getAiStrengths(candidate).length > 0 || getAiGaps(candidate).length > 0) && (
                              <div className="grid gap-3 md:grid-cols-2">
                                <div className="rounded-lg border border-emerald-200 bg-white p-4 shadow-sm">
                                  <p className="mb-2 text-sm font-semibold text-emerald-900">{scoringLabel("AI Matching Evidence", scoringLanguage)}</p>
                                  {getAiStrengths(candidate).length ? (
                                    <ul className="space-y-2 text-sm leading-6 text-emerald-800">
                                      {getAiStrengths(candidate).map((item, itemIndex) => (
                                        <li key={`${candidate.scan_id}-ranking-ai-strength-${itemIndex}`}>{item}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-emerald-700">No AI matching evidence generated yet.</p>
                                  )}
                                </div>

                                <div className="rounded-lg border border-rose-200 bg-white p-4 shadow-sm">
                                  <p className="mb-2 text-sm font-semibold text-rose-900">{scoringLabel("AI Skill Gaps", scoringLanguage)}</p>
                                  {getAiGaps(candidate).length ? (
                                    <ul className="space-y-2 text-sm leading-6 text-rose-800">
                                      {getAiGaps(candidate).map((item, itemIndex) => (
                                        <li key={`${candidate.scan_id}-ranking-ai-gap-${itemIndex}`}>{item}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-rose-700">No AI gaps generated yet.</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {getSkillEvidence(candidate).length > 0 && (
                              <div className="mt-4 rounded-lg border border-indigo-200 bg-white p-4 shadow-sm">
                                <p className="mb-3 text-sm font-semibold text-indigo-950">
                                  Skill Evidence Score
                                </p>
                                <div className="grid gap-3 md:grid-cols-2">
                                  {getSkillEvidence(candidate).map((item, itemIndex) => {
                                    const confidence = Math.max(0, Math.min(100, Number(item.confidence) || 0));

                                    return (
                                      <div key={`${candidate.scan_id}-skill-evidence-${itemIndex}`} className="rounded-md bg-indigo-50/70 p-3">
                                        <div className="flex items-center justify-between gap-3">
                                          <span className="text-sm font-semibold text-slate-900">
                                            {item.skill || "Skill"}
                                          </span>
                                          <span className="text-sm font-bold text-indigo-800">
                                            {confidence}%
                                          </span>
                                        </div>
                                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-indigo-100">
                                          <div
                                            className="h-full rounded-full bg-indigo-600"
                                            style={{ width: `${confidence}%` }}
                                          />
                                        </div>
                                        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                          {item.status || "evidence"}
                                        </p>
                                        {item.evidence && (
                                          <p className="mt-1 text-xs leading-5 text-slate-600">
                                            {item.evidence}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {selectedDecisionTab === "language" && getLanguageMatch(candidate).length > 0 && (
                          <div className="rounded-xl border-2 border-teal-400 bg-teal-100/60 p-4 shadow-[0_0_0_3px_rgba(20,184,166,0.12)] lg:col-span-2">
                            <div className="mb-4 rounded-lg bg-teal-800 px-4 py-3 text-white shadow-sm">
                              <p className="mt-1 text-base font-bold">Language & Qualification Fit</p>
                              <p className="mt-1 text-xs leading-5 text-teal-50">Language requirements, degree evidence, certifications, and qualification signals.</p>
                            </div>

                            <div className="rounded-lg border border-teal-200 bg-white p-4 shadow-sm">
                              <p className="mb-3 text-sm font-semibold text-teal-950">
                                {scoringLabel("Language Match", scoringLanguage)}
                              </p>
                              <div className="grid gap-3 md:grid-cols-2">
                                {getLanguageMatch(candidate).map((item, itemIndex) => {
                                  const fitScore = getLanguageFitScore(item);

                                  return (
                                    <div key={`${candidate.scan_id}-language-match-${itemIndex}`} className="rounded-md bg-teal-50/70 p-3">
                                      <div className="flex items-center justify-between gap-3">
                                        <span className="text-sm font-semibold text-slate-900">
                                          {item.language || "Language"}
                                        </span>
                                        <span className="text-sm font-bold text-teal-800">
                                          {fitScore}%
                                        </span>
                                      </div>
                                      <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide">
                                        {item.required_level && (
                                          <span className="rounded-full bg-teal-100 px-2 py-1 text-teal-800">
                                            Required: {item.required_level}
                                          </span>
                                        )}
                                        <span className="rounded-full bg-white px-2 py-1 text-teal-700">
                                          Match: {item.status || "language signal"}
                                        </span>
                                      </div>
                                      {item.evidence && (
                                        <p className="mt-1 text-xs leading-5 text-slate-600">
                                          {item.evidence}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {selectedDecisionTab === "ai" && (candidate.ai_summary || (candidate.interview_questions || []).length > 0) && (
                          <div className="rounded-lg border border-indigo-200 bg-white p-4 shadow-sm lg:col-span-2">
                            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <p className="text-sm font-semibold text-indigo-950">
                                Suggested Interview Questions
                              </p>
                              {!(candidate.interview_questions || []).length && (
                                <button
                                  type="button"
                                  onClick={() => requestInterviewQuestions(candidate)}
                                  disabled={loadingQuestionId === candidate.scan_id}
                                  className="inline-flex min-h-[32px] items-center justify-center rounded-lg bg-indigo-700 px-3 text-xs font-semibold text-white transition hover:bg-indigo-800 disabled:opacity-60"
                                >
                                  {loadingQuestionId === candidate.scan_id ? "Generating..." : "Generate questions"}
                                </button>
                              )}
                            </div>
                            {(candidate.interview_questions || []).length > 0 ? (
                              <div className="space-y-3">
                                {candidate.interview_questions.map((item, itemIndex) => (
                                  <div key={`${candidate.scan_id}-interview-${itemIndex}`} className="rounded-md bg-white/70 p-3">
                                    {item.gap && (
                                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                        Gap: {item.gap}
                                      </p>
                                    )}
                                    <ul className="space-y-2 text-sm leading-6 text-indigo-950">
                                      {(item.questions || []).map((question, questionIndex) => (
                                        <li key={`${candidate.scan_id}-interview-${itemIndex}-${questionIndex}`}>
                                          {question}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="rounded-md bg-white/70 p-3 text-sm text-indigo-900">
                                Generate these only when you are preparing to interview this AI-enriched candidate.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg border border-amber-200 bg-white p-4 shadow-sm xl:sticky xl:top-4 xl:self-start">
                        <p className="mb-2 text-sm font-semibold text-amber-900">Recruiter Notes</p>
                        <textarea
                          value={notes[candidate.scan_id] ?? ""}
                          onChange={(event) =>
                            setNotes((prev) => ({
                              ...prev,
                              [candidate.scan_id]: event.target.value,
                            }))
                          }
                          placeholder="Add recruiter notes..."
                          rows={5}
                          className="w-full resize-y rounded-lg border border-amber-200 bg-white p-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                        />
                        <button
                          type="button"
                          onClick={() => saveNote(candidate.scan_id)}
                          disabled={savingNoteId === candidate.scan_id}
                          className="mt-2 inline-flex min-h-[32px] items-center justify-center rounded-lg bg-amber-500 px-3 text-xs font-semibold text-white transition hover:bg-amber-600 disabled:opacity-50"
                        >
                          {savingNoteId === candidate.scan_id ? "Saving..." : "Save Note"}
                        </button>

                        <div className="mt-4 rounded-lg border border-white bg-white/80 p-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">Train RecruitGlance ranking</p>
                              <p className="mt-1 text-xs leading-5 text-slate-500">
                                Tell the system whether this AI result helped your decision.
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => saveFeedback(candidate, "useful")}
                                disabled={savingFeedbackId === candidate.scan_id}
                                className={`inline-flex min-h-[34px] items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold transition disabled:opacity-50 ${
                                  candidate.recruiter_feedback === "useful"
                                    ? "bg-emerald-700 text-white"
                                    : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                }`}
                              >
                                <ThumbsUp size={14} />
                                Useful
                              </button>
                              <button
                                type="button"
                                onClick={() => saveFeedback(candidate, "incorrect")}
                                disabled={savingFeedbackId === candidate.scan_id}
                                className={`inline-flex min-h-[34px] items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold transition disabled:opacity-50 ${
                                  candidate.recruiter_feedback === "incorrect"
                                    ? "bg-rose-700 text-white"
                                    : "bg-rose-50 text-rose-800 hover:bg-rose-100"
                                }`}
                              >
                                <ThumbsDown size={14} />
                                Needs correction
                              </button>
                            </div>
                          </div>
                          {candidate.recruiter_feedback && (
                            <p className="mt-3 text-xs font-medium text-slate-500">
                              Saved feedback: {candidate.recruiter_feedback.replace("_", " ")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {emailModalType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <section className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
              <div>
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                  emailModalType === "shortlisted"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }`}>
                  <Mail size={14} />
                  {emailModalType === "shortlisted" ? "Shortlist email" : "Rejection email"}
                </span>
                <h3 className="mt-3 text-xl font-semibold text-slate-950">
                  {emailModalType === "shortlisted" ? "Email shortlisted candidates" : "Email rejected candidates"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Review the candidates for this job, untick anyone you do not want to include, then send the configured email template.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEmailModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                aria-label="Close email review"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[58vh] overflow-y-auto p-5">
              {getEmailCandidates(emailModalType).length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center">
                  <Mail className="mx-auto text-slate-300" size={32} />
                  <p className="mt-3 text-sm font-semibold text-slate-800">
                    No {emailModalType === "shortlisted" ? "shortlisted" : "rejected"} candidates yet
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Mark candidates as {emailModalType === "shortlisted" ? "shortlisted" : "rejected"} first, then come back to send emails.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getEmailCandidates(emailModalType).map((candidate) => (
                    <label
                      key={`email-modal-${candidate.scan_id}`}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedActionIds.includes(candidate.scan_id)}
                        onChange={() => toggleActionSelection(candidate)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-700"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                          <span className={`w-fit rounded-full border px-2 py-1 text-xs font-bold ${scoreClass(candidate.match_score || 0)}`}>
                            {Math.round(candidate.match_score || 0)}%
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {candidate.candidate_email ? (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                              {candidate.candidate_email}
                            </span>
                          ) : (
                            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                              Missing email
                            </span>
                          )}
                          {renderEmailStatus(candidate)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-600">
                {selectedActionIds.length} candidate{selectedActionIds.length === 1 ? "" : "s"} selected for email.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={closeEmailModal}
                  className="inline-flex min-h-[38px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => sendBatchEmails(emailModalType)}
                  disabled={!selectedActionIds.length || sendingEmailType !== null}
                  className={`inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                    emailModalType === "shortlisted"
                      ? "bg-emerald-950 hover:bg-emerald-900"
                      : "bg-rose-700 hover:bg-rose-800"
                  }`}
                >
                  <Mail size={16} />
                  {sendingEmailType ? "Sending..." : emailModalType === "shortlisted" ? "Send Shortlist Email" : "Send Rejection Email"}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      <CandidateEmailSendConsentModal
        open={emailSendConsent.open}
        provider={emailSendConsent.provider}
        onCancel={() => closeCandidateEmailSendConsent(false)}
        onConfirm={() => closeCandidateEmailSendConsent(true)}
      />

      {showCompare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <section className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Candidate comparison
                </p>
                <h3 className="mt-1 text-xl font-semibold text-slate-950">
                  Side-by-side shortlist review
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCompare(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                aria-label="Close comparison"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[calc(90vh-88px)] overflow-auto p-5">
              <div className="space-y-6">
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full min-w-[820px] divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="w-52 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Decision factor
                        </th>
                        {comparedCandidates.map((candidate) => (
                          <th key={`compare-head-${candidate.scan_id}`} className="px-4 py-3 text-left">
                            <div className="max-w-64">
                              <p className="truncate text-sm font-semibold text-slate-950">
                                {candidate.filename}
                              </p>
                              <p className="mt-1 text-xs font-medium text-slate-400">
                                Scanned {formatDate(candidate.created_at)}
                              </p>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Match score</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-score-${candidate.scan_id}`} className="px-4 py-4">
                            <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-bold ${scoreClass(candidate.match_score || 0)}`}>
                              {Math.round(candidate.match_score || 0)}%
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Shortlist status</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-shortlist-${candidate.scan_id}`} className="px-4 py-4">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              candidate.is_shortlisted
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}>
                              {candidate.is_shortlisted ? "Shortlisted" : "Needs review"}
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Education & certifications</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-qualification-${candidate.scan_id}`} className="px-4 py-4 text-slate-700">
                            {getTopQualification(candidate) || "Not detected"}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Matched skills</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-matched-${candidate.scan_id}`} className="px-4 py-4 text-slate-700">
                            {formatListValue(candidate.matching_skills)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Skill gaps</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-gaps-${candidate.scan_id}`} className="px-4 py-4 text-slate-700">
                            {formatListValue(candidate.missing_skills)}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">AI decision note</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-summary-${candidate.scan_id}`} className="px-4 py-4">
                            <p className="line-clamp-4 text-sm leading-6 text-slate-700">
                              {candidate.ai_summary || "No AI summary available yet."}
                            </p>
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-4 py-4 font-semibold text-slate-700">Recruiter note</td>
                        {comparedCandidates.map((candidate) => (
                          <td key={`compare-note-${candidate.scan_id}`} className="px-4 py-4">
                            <p className="line-clamp-3 text-sm leading-6 text-slate-700">
                              {candidate.recruiter_note || "No notes"}
                            </p>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {compareSkillRows.length > 0 && (
                  <div>
                    <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-950">Skill-by-skill comparison</h4>
                        <p className="text-xs text-slate-500">
                          Shows whether each selected candidate matches, misses, or does not mention the same requirement.
                        </p>
                      </div>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full min-w-[760px] divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="w-52 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Skill
                            </th>
                            {comparedCandidates.map((candidate) => (
                              <th key={`skill-head-${candidate.scan_id}`} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {candidate.filename}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {compareSkillRows.map((skill) => (
                            <tr key={`skill-row-${skill}`}>
                              <td className="px-4 py-3 font-semibold text-slate-800">{skill}</td>
                              {comparedCandidates.map((candidate) => {
                                const cell = getSkillComparisonCell(candidate, skill);

                                return (
                                  <td key={`skill-cell-${candidate.scan_id}-${skill}`} className="px-4 py-3">
                                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${cell.className}`}>
                                      {cell.label}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {compareCriteriaRows.length > 0 && (
                  <div>
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-slate-950">Recruiter criteria comparison</h4>
                      <p className="text-xs text-slate-500">
                        Compares must-have and preferred criteria when custom job scoring was used.
                      </p>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full min-w-[820px] divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="w-56 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Criteria
                            </th>
                            {comparedCandidates.map((candidate) => (
                              <th key={`criteria-head-${candidate.scan_id}`} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {candidate.filename}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {compareCriteriaRows.map((row) => (
                            <tr key={`criteria-row-${row.type}-${row.skill}`}>
                              <td className="px-4 py-3">
                                <span className="block font-semibold text-slate-800">{row.skill}</span>
                                <span className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                                  {row.type}
                                </span>
                              </td>
                              {comparedCandidates.map((candidate) => {
                                const cell = getCriteriaComparisonCell(candidate, row);

                                return (
                                  <td key={`criteria-cell-${candidate.scan_id}-${row.type}-${row.skill}`} className="px-4 py-3 align-top">
                                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${cell.className}`}>
                                      {cell.label}
                                    </span>
                                    {cell.detail && (
                                      <p className="mt-2 line-clamp-3 text-xs leading-5 text-slate-500">
                                        {cell.detail}
                                      </p>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 border-t border-slate-200 pt-5">
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-950">Detailed evidence cards</h4>
                  <p className="text-xs text-slate-500">
                    Open these when you need the full AI summary, evidence scores, language match, and interview questions.
                  </p>
                </div>
                <div className="grid gap-4 lg:grid-cols-3">
                {comparedCandidates.map((candidate) => (
                  <article key={`compare-${candidate.scan_id}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="truncate text-base font-semibold text-slate-950">
                          {candidate.filename}
                        </h4>
                        <p className="mt-1 text-xs text-slate-400">
                          Scanned {formatDate(candidate.created_at)}
                        </p>
                        {candidate.top_qualification && (
                          <p className="mt-2 rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                            {candidate.top_qualification}
                          </p>
                        )}
                      </div>
                      <span className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreClass(candidate.match_score || 0)}`}>
                        {Math.round(candidate.match_score || 0)}%
                      </span>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">AI Summary</p>
                        <p className="rounded-lg bg-sky-50 p-3 text-sm leading-6 text-sky-900">
                          {candidate.ai_summary || "No AI summary available yet."}
                        </p>
                      </div>

                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">{scoringLabel("Detected Skill Evidence", scoringLanguage)}</p>
                        <div className="flex flex-wrap gap-2">
                          {(candidate.matching_skills || []).length ? (
                            candidate.matching_skills.slice(0, 10).map((skill) => (
                              <span key={`compare-${candidate.scan_id}-match-${skill}`} className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-slate-500">No strong matches detected</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">{scoringLabel("Unconfirmed Skill Evidence", scoringLanguage)}</p>
                        <div className="flex flex-wrap gap-2">
                          {(candidate.missing_skills || []).length ? (
                            candidate.missing_skills.slice(0, 10).map((skill) => (
                              <span key={`compare-${candidate.scan_id}-gap-${skill}`} className="rounded-full bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-slate-500">No major gaps detected</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">Skill Evidence</p>
                        {getSkillEvidence(candidate).length ? (
                          <div className="space-y-2">
                            {getSkillEvidence(candidate).slice(0, 6).map((item, itemIndex) => {
                              const confidence = Math.max(0, Math.min(100, Number(item.confidence) || 0));

                              return (
                                <div key={`compare-${candidate.scan_id}-skill-evidence-${itemIndex}`} className="rounded-lg bg-indigo-50 p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-semibold text-indigo-950">
                                      {item.skill || "Skill"}
                                    </span>
                                    <span className="text-sm font-bold text-indigo-800">
                                      {confidence}%
                                    </span>
                                  </div>
                                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-indigo-100">
                                    <div
                                      className="h-full rounded-full bg-indigo-600"
                                      style={{ width: `${confidence}%` }}
                                    />
                                  </div>
                                  <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                    {item.status || "evidence"}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
                            No skill evidence generated yet.
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">{scoringLabel("Language Match", scoringLanguage)}</p>
                        {getLanguageMatch(candidate).length ? (
                          <div className="space-y-2">
                            {getLanguageMatch(candidate).slice(0, 3).map((item, itemIndex) => {
                              const fitScore = getLanguageFitScore(item);

                              return (
                                <div key={`compare-${candidate.scan_id}-language-match-${itemIndex}`} className="rounded-lg bg-emerald-50 p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-semibold text-emerald-950">
                                      {item.language || "Language"}
                                    </span>
                                    <span className="text-sm font-bold text-emerald-800">
                                      {fitScore}%
                                    </span>
                                  </div>
                                  <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide">
                                    {item.required_level && (
                                      <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">
                                        Required: {item.required_level}
                                      </span>
                                    )}
                                    <span className="rounded-full bg-white px-2 py-1 text-emerald-700">
                                      Match: {item.status || "language signal"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
                            No language match generated yet.
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="mb-2 text-sm font-semibold text-slate-800">Interview Questions</p>
                        {(candidate.interview_questions || []).length ? (
                          <div className="space-y-3">
                            {candidate.interview_questions.slice(0, 3).map((item, itemIndex) => (
                              <div key={`compare-${candidate.scan_id}-question-${itemIndex}`} className="rounded-lg bg-indigo-50 p-3">
                                {item.gap && (
                                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                                    {item.gap}
                                  </p>
                                )}
                                <ul className="space-y-2 text-sm leading-6 text-indigo-950">
                                  {(item.questions || []).slice(0, 2).map((question, questionIndex) => (
                                    <li key={`compare-${candidate.scan_id}-question-${itemIndex}-${questionIndex}`}>
                                      {question}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
                            No interview questions generated yet.
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Shortlist</p>
                          <p className="mt-1 font-semibold text-slate-900">
                            {candidate.is_shortlisted ? "Yes" : "No"}
                          </p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</p>
                          <p className="mt-1 line-clamp-3 text-slate-700">
                            {candidate.recruiter_note || "No notes"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );

  if (loading || !user) {
    return <p className="text-center mt-20">Loading...</p>;
  }

  if (fetching) {
    return (
      <DashboardLayout
        user={user}
        eyebrow="Job decision view"
        title="Rankings"
        subtitle="Review every scanned candidate for this job across all scan batches"
        leftPanel={leftPanel}
        mainContent={
          <section className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm font-semibold text-slate-700">Loading rankings...</p>
            <p className="mt-2 text-sm text-slate-500">
              Preparing the job decision view without changing the page layout.
            </p>
          </section>
        }
      />
    );
  }

  return (
    <DashboardLayout
      user={user}
      eyebrow="Job decision view"
      title="Rankings"
      subtitle="Review every scanned candidate for this job across all scan batches"
      leftPanel={leftPanel}
      headerAction={rankingsHeaderAction}
      mainContent={mainContent}
    />
  );
}
